import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Package, Users, FileText, LogOut, Plus, Camera, X, Pencil, Trash2, 
  LayoutDashboard, Send, Menu, ChevronRight, Search, DollarSign,
  Eye, Share2, Check, Download, CreditCard, TrendingUp, Calendar, ArrowUpDown, UploadCloud, MessageSquare
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import BulkUpload from '../components/admin/BulkUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatInvoiceDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [feedbackList, setFeedbackList] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false); // Start closed on mobile
  
  // Dialog states
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  
  // Scanner states
  const [scanning, setScanning] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerInitialized = useRef(false);

  // Search states
  const [packageCustomerSearch, setPackageCustomerSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerListSearch, setCustomerListSearch] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');

  const [migratingAddresses, setMigratingAddresses] = useState(false);
  const [signupsPaused, setSignupsPaused] = useState(false);
  const [togglingSignups, setTogglingSignups] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/signups`)
      .then(res => setSignupsPaused(!!res.data.paused))
      .catch(() => {});
  }, []);

  const handleToggleSignups = async () => {
    setTogglingSignups(true);
    try {
      const token = localStorage.getItem('token');
      const next = !signupsPaused;
      const res = await axios.post(`${API}/admin/settings/signups`, { paused: next }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSignupsPaused(!!res.data.paused);
      toast.success(res.data.paused ? 'Signups PAUSED — new customers will see a notice' : 'Signups RESUMED — new customers can register again');
    } catch (err) {
      toast.error('Failed to update signup status: ' + (err.response?.data?.detail || err.message));
    }
    setTogglingSignups(false);
  };

  const handleMigrateAddresses = async () => {
    if (!window.confirm('This will update ALL customer addresses to 836 NE 30th CT Building B, Oakland Park, FL 33334-2625 — EXCEPT DAC339 which stays at 5161 NW 79th Ave 5 D, Doral FL 33166. Every name will end with "DAC". Continue?')) return;
    setMigratingAddresses(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/migrate-addresses`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data.message}`);
    } catch (err) {
      toast.error('Migration failed: ' + (err.response?.data?.detail || err.message));
    }
    setMigratingAddresses(false);
  };

  // Finance states
  const [financeTab, setFinanceTab] = useState('balances');
  const [balanceSummary, setBalanceSummary] = useState([]);
  const [financePayments, setFinancePayments] = useState([]);
  const [outstandingBalances, setOutstandingBalances] = useState([]);
  const [financialReport, setFinancialReport] = useState(null);
  const [statementCustomer, setStatementCustomer] = useState('');
  const [statementData, setStatementData] = useState(null);
  const [statementDateRange, setStatementDateRange] = useState({ start: '', end: '' });
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ customer_email: '', amount: '', date: new Date().toISOString().split('T')[0], applied_to: '', method: 'cash', notes: '' });
  const [financeSearch, setFinanceSearch] = useState('');
  const [financeLoading, setFinanceLoading] = useState(false);

  // Memoise the filtered balance summary — used in both mobile card view and desktop table
  const filteredBalanceSummary = useMemo(() => {
    const q = financeSearch.toLowerCase();
    if (!q) return balanceSummary;
    return balanceSummary.filter(s =>
      s.customer_name.toLowerCase().includes(q) ||
      s.customer_email.toLowerCase().includes(q)
    );
  }, [balanceSummary, financeSearch]);

  // ========== FINANCE FUNCTIONS ==========
  const fetchFinanceData = useCallback(async (tab) => {
    setFinanceLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (tab === 'balances') {
        const res = await axios.get(`${API}/admin/finance/balance-summary`, { headers });
        setBalanceSummary(res.data.summaries);
      } else if (tab === 'payments') {
        const res = await axios.get(`${API}/admin/finance/payments`, { headers });
        setFinancePayments(res.data);
      } else if (tab === 'outstanding') {
        const res = await axios.get(`${API}/admin/finance/outstanding`, { headers });
        setOutstandingBalances(res.data);
      } else if (tab === 'reports') {
        const res = await axios.get(`${API}/admin/finance/reports`, { headers });
        setFinancialReport(res.data);
      }
    } catch (err) {
      toast.error('Failed to load finance data');
    }
    setFinanceLoading(false);
  }, []);

  const fetchFeedback = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/feedback`, { headers: { Authorization: `Bearer ${token}` } });
      setFeedbackList(res.data);
    } catch (err) {
      toast.error('Failed to load feedback');
    }
  }, []);

  const fetchCustomerStatement = async () => {
    if (!statementCustomer) return;
    setFinanceLoading(true);
    const token = localStorage.getItem('token');
    try {
      const params = new URLSearchParams();
      if (statementDateRange.start) params.append('start_date', statementDateRange.start);
      if (statementDateRange.end) params.append('end_date', statementDateRange.end);
      const res = await axios.get(`${API}/admin/finance/customer-statement/${statementCustomer}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatementData(res.data);
    } catch (err) {
      toast.error('Failed to load statement');
    }
    setFinanceLoading(false);
  };

  const handleCreatePayment = async () => {
    if (!paymentForm.customer_email || !paymentForm.amount) {
      toast.error('Customer and amount required');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/finance/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Payment recorded');
      setShowPaymentDialog(false);
      setPaymentForm({ customer_email: '', amount: '', date: new Date().toISOString().split('T')[0], applied_to: '', method: 'cash', notes: '' });
      fetchFinanceData(financeTab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const downloadPdf = async (endpoint, filename) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/admin/finance/pdf/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  useEffect(() => {
    if (activeSection === 'finance') {
      fetchFinanceData(financeTab);
    }
  }, [activeSection, financeTab, fetchFinanceData]);

  useEffect(() => {
    if (activeSection === 'feedback') {
      fetchFeedback();
    }
  }, [activeSection, fetchFeedback]);

  // Delete confirmation states
  const [deleteCustomerConfirm, setDeleteCustomerConfirm] = useState(null);
  const [deletePackageConfirm, setDeletePackageConfirm] = useState(null);

  // Form states
  const [packageForm, setPackageForm] = useState({
    tracking_number: '',
    customer_email: '',
    carrier: '',
    dimensions: '',
    weight: '',
    estimated_delivery: '',
    photo_path: '',
  });
  const [packagePhotoPreview, setPackagePhotoPreview] = useState('');
  const [uploadingPackagePhoto, setUploadingPackagePhoto] = useState(false);

  const [invoiceForm, setInvoiceForm] = useState({
    customer_email: '',
    items: []
  });
  const [viewAdminInvoice, setViewAdminInvoice] = useState(null);
  const [selectedPackageIds, setSelectedPackageIds] = useState([]);

  const [invoiceItems, setInvoiceItems] = useState({
    freight_qty: '',
    freight_rate: '',
    handling: '',
    duty: '',
    insurance: ''
  });

  // Custom invoice items
  const [customItems, setCustomItems] = useState([]);

  const [broadcastForm, setBroadcastForm] = useState({
    emails: '',
    subject: '',
    message: ''
  });
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editCustomerForm, setEditCustomerForm] = useState({ name: '', email: '' });
  
  const [transitStartDate, setTransitStartDate] = useState('');
  const [transitEndDate, setTransitEndDate] = useState('');
  const [updatingTransit, setUpdatingTransit] = useState(false);
  const [customerPackages, setCustomerPackages] = useState([]);
  const [invoiceTrackingNumbers, setInvoiceTrackingNumbers] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [customersRes, packagesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/admin/customers`, { headers }),
        axios.get(`${API}/admin/packages`, { headers }),
        axios.get(`${API}/admin/invoices`, { headers })
      ]);

      setCustomers(customersRes.data);
      setPackages(packagesRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup scanners on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const filteredPackageCustomers = packageCustomerSearch.length > 0
    ? customers.filter(customer => 
        customer.name.toLowerCase().includes(packageCustomerSearch.toLowerCase()) ||
        customer.email.toLowerCase().includes(packageCustomerSearch.toLowerCase()) ||
        (customer.us_address?.unique_id || '').toLowerCase().includes(packageCustomerSearch.toLowerCase())
      )
    : customers;

  const filteredCustomers = customerSearch.length > 0
    ? customers.filter(customer => 
        customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
        customer.email.toLowerCase().includes(customerSearch.toLowerCase()) ||
        (customer.us_address?.unique_id || '').toLowerCase().includes(customerSearch.toLowerCase())
      )
    : customers;

  const filteredCustomerList = customerListSearch.length > 0
    ? customers.filter(customer => 
        customer.name.toLowerCase().includes(customerListSearch.toLowerCase()) ||
        customer.email.toLowerCase().includes(customerListSearch.toLowerCase()) ||
        (customer.us_address?.unique_id || '').toLowerCase().includes(customerListSearch.toLowerCase()) ||
        (customer.phone || '').includes(customerListSearch)
      )
    : customers;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  // Scanner functions
  const startScanner = async () => {
    setScanning(true);
    
    // Wait for DOM to update
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      const scannerElement = document.getElementById("barcode-scanner");
      if (!scannerElement) {
        toast.error('Scanner not ready. Please try again.');
        setScanning(false);
        return;
      }

      const devices = await Html5Qrcode.getCameras();
      if (!devices || devices.length === 0) {
        toast.error('No camera found');
        setScanning(false);
        return;
      }

      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
          html5QrCodeRef.current.clear();
        } catch (e) {
          // Scanner may already be stopped — safe to ignore
        }
        html5QrCodeRef.current = null;
      }

      const html5QrCode = new Html5Qrcode("barcode-scanner");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 150 } },
        (decodedText) => {
          setPackageForm(prev => ({ ...prev, tracking_number: decodedText }));
          toast.success(`Scanned: ${decodedText}`);
          stopScanner();
        },
        () => {}
      );
      scannerInitialized.current = true;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Scanner error:', err);
      toast.error('Scanner error. Check camera permissions.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    setScanning(false);
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Scanner may already be stopped — safe to ignore
      }
      html5QrCodeRef.current = null;
    }
    scannerInitialized.current = false;
  };

  const handlePackagePhotoSelect = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please select a JPEG, PNG or WEBP image');
      return;
    }
    setUploadingPackagePhoto(true);
    setPackagePhotoPreview(URL.createObjectURL(file));
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/packages/upload-photo`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPackageForm(prev => ({
        ...prev,
        photo_path: res.data.photo_path,
        tracking_number: res.data.extracted_tracking || prev.tracking_number,
      }));
      if (res.data.extracted_tracking) {
        toast.success(`Detected tracking: ${res.data.extracted_tracking}`);
      } else {
        toast.info('Photo uploaded — please type the tracking number manually');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
      setPackagePhotoPreview('');
    }
    setUploadingPackagePhoto(false);
  };

  const handleCreatePackage = async (e) => {
    e.preventDefault();
    if (scanning) await stopScanner();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/packages`, packageForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Package created!');
      setShowPackageDialog(false);
      setPackageForm({ tracking_number: '', customer_email: '', carrier: '', dimensions: '', weight: '', estimated_delivery: '', photo_path: '' });
      setPackagePhotoPreview('');
      setPackageCustomerSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create package');
    }
  };

  const handleUpdatePackageStatus = async (packageId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/packages/${packageId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  // Arrival Scanner Functions

  const handleDeletePackage = async (packageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Package deleted');
      setDeletePackageConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteCustomer = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/customers/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer deleted');
      setDeleteCustomerConfirm(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Custom invoice item functions
  const addCustomItem = (type = 'charge') => {
    setCustomItems([...customItems, { id: Date.now(), name: '', amount: '', type }]);
  };

  const updateCustomItem = (index, field, value) => {
    const updated = [...customItems];
    updated[index][field] = value;
    setCustomItems(updated);
  };

  const removeCustomItem = (index) => {
    setCustomItems(customItems.filter((_, i) => i !== index));
  };

  const calculateInvoiceTotal = () => {
    let total = 0;
    if (invoiceItems.freight_qty && invoiceItems.freight_rate) {
      total += parseFloat(invoiceItems.freight_qty) * parseFloat(invoiceItems.freight_rate);
    }
    if (invoiceItems.handling) total += parseFloat(invoiceItems.handling);
    if (invoiceItems.duty) total += parseFloat(invoiceItems.duty);
    if (invoiceItems.insurance) total += parseFloat(invoiceItems.insurance);
    
    // Add custom items (credits subtract)
    customItems.forEach(item => {
      if (item.amount) {
        const amt = Math.abs(parseFloat(item.amount));
        total += item.type === 'credit' ? -amt : amt;
      }
    });
    
    return total.toFixed(2);
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const items = [];
      let totalAmount = 0;
      
      if (invoiceItems.freight_qty && invoiceItems.freight_rate) {
        const freightAmount = parseFloat(invoiceItems.freight_qty) * parseFloat(invoiceItems.freight_rate);
        items.push({ description: `Freight (${invoiceItems.freight_qty} lbs @ $${invoiceItems.freight_rate}/lb)`, amount: freightAmount });
        totalAmount += freightAmount;
      }
      
      if (invoiceItems.handling) {
        const amt = parseFloat(invoiceItems.handling);
        items.push({ description: 'Handling Fee', amount: amt });
        totalAmount += amt;
      }
      
      if (invoiceItems.duty) {
        const amt = parseFloat(invoiceItems.duty);
        items.push({ description: 'Duty', amount: amt });
        totalAmount += amt;
      }

      if (invoiceItems.insurance) {
        const amt = parseFloat(invoiceItems.insurance);
        items.push({ description: 'Insurance', amount: amt });
        totalAmount += amt;
      }

      // Add custom items (credits subtract)
      customItems.forEach(item => {
        if (item.name && item.amount) {
          const amt = Math.abs(parseFloat(item.amount));
          const signed = item.type === 'credit' ? -amt : amt;
          items.push({ description: item.type === 'credit' ? `Credit: ${item.name}` : item.name, amount: signed });
          totalAmount += signed;
        }
      });
      
      if (items.length === 0) {
        toast.error('Add at least one item');
        return;
      }
      
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);
      
      await axios.post(`${API}/admin/invoices`, {
        customer_email: invoiceForm.customer_email,
        amount: totalAmount,
        due_date: dueDate.toISOString().split('T')[0],
        items: items,
        tracking_numbers: invoiceTrackingNumbers
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Invoice created!');
      setShowInvoiceDialog(false);
      setInvoiceForm({ customer_email: '', items: [] });
      setInvoiceItems({ freight_qty: '', freight_rate: '', handling: '', duty: '', insurance: '' });
      setCustomItems([]);
      setInvoiceTrackingNumbers('');
      setCustomerSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleBroadcastMessage = async (e) => {
    e.preventDefault();
    setSendingBroadcast(true);
    
    try {
      const token = localStorage.getItem('token');
      const emailList = broadcastForm.emails.split(/[\n,]/).map(e => e.trim()).filter(e => e.length > 0);
      
      const response = await axios.post(`${API}/admin/broadcast`, {
        emails: emailList,
        subject: broadcastForm.subject,
        message: broadcastForm.message
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Sent to ${response.data.sent_count} recipients!`);
      setBroadcastForm({ emails: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setEditCustomerForm({ name: customer.name, email: customer.email });
    setShowEditCustomerDialog(true);
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/customers/${selectedCustomer.user_id}`, editCustomerForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer updated!');
      setShowEditCustomerDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleBulkTransitUpdate = async () => {
    if (!transitStartDate || !transitEndDate) {
      toast.error('Select both dates');
      return;
    }
    
    setUpdatingTransit(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/packages/bulk-transit`, 
        { start_date: transitStartDate, end_date: transitEndDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setTransitStartDate('');
      setTransitEndDate('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setUpdatingTransit(false);
    }
  };

  const [includeOldShipments, setIncludeOldShipments] = useState(false);
  const [currentShipmentInfo, setCurrentShipmentInfo] = useState(null);
  const [shipmentBusy, setShipmentBusy] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/admin/shipments/current`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCurrentShipmentInfo(res.data.shipment))
      .catch(() => {});
  }, []);

  const handleStartNewShipment = async () => {
    const name = window.prompt('Optional name for the new shipment (leave blank for auto-generated):', '');
    if (name === null) return; // user cancelled
    setShipmentBusy(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/shipments/new`, { name: name.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCurrentShipmentInfo(res.data.shipment);
      toast.success(`Started: ${res.data.shipment.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start shipment');
    }
    setShipmentBusy(false);
  };

  const fetchCustomerPackages = async (customerEmail, includeOldOverride) => {
    if (!customerEmail) {
      setCustomerPackages([]);
      setSelectedPackageIds([]);
      setInvoiceTrackingNumbers('');
      setInvoiceItems(prev => ({ ...prev, freight_qty: '' }));
      return;
    }

    const includeOld = includeOldOverride !== undefined ? includeOldOverride : includeOldShipments;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/customer-packages/${customerEmail}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: includeOld ? { include_old: true } : {},
      });

      const pkgs = response.data.packages;
      setCustomerPackages(pkgs);
      // Auto-select all packages
      const allIds = pkgs.map(p => p.tracking_number);
      setSelectedPackageIds(allIds);
      setInvoiceTrackingNumbers(allIds.join(', '));
      setInvoiceItems(prev => ({ ...prev, freight_qty: response.data.total_weight.toString() }));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to fetch packages');
    }
  };

  const togglePackageSelection = (trackingNumber, weight) => {
    setSelectedPackageIds(prev => {
      const isSelected = prev.includes(trackingNumber);
      const next = isSelected ? prev.filter(id => id !== trackingNumber) : [...prev, trackingNumber];
      setInvoiceTrackingNumbers(next.join(', '));
      // Recalculate total weight from selected packages
      const selectedPkgs = customerPackages.filter(p => next.includes(p.tracking_number));
      const totalWeight = selectedPkgs.reduce((sum, p) => {
        const w = parseFloat(String(p.weight || '0').replace(/[^\d.]/g, ''));
        return sum + (isNaN(w) ? 0 : w);
      }, 0);
      setInvoiceItems(prev => ({ ...prev, freight_qty: totalWeight > 0 ? totalWeight.toString() : '' }));
      return next;
    });
  };

  const handleMarkInvoicePaid = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/invoices/${invoiceId}`, { status: 'paid' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice marked as paid!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      received_by_dac: 'bg-blue-50 text-blue-700 border-blue-200',
      in_transit: 'bg-amber-50 text-amber-700 border-amber-200',
      arrived_in_guyana: 'bg-purple-50 text-purple-700 border-purple-200',
      delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getStatusLabel = (status) => {
    const labels = {
      received_by_dac: 'At Warehouse',
      in_transit: 'In Transit',
      arrived_in_guyana: 'Arrived GY',
      delivered: 'Delivered'
    };
    return labels[status] || status.replace('_', ' ');
  };

  const getScanResultStyle = (result) => {
    if (result.success) return { bg: 'bg-emerald-50 border border-emerald-200', text: 'text-emerald-700', icon: '✅ ' };
    if (result.already_arrived) return { bg: 'bg-blue-50 border border-blue-200', text: 'text-blue-700', icon: 'ℹ️ ' };
    return { bg: 'bg-amber-50 border border-amber-200', text: 'text-amber-700', icon: '⚠️ ' };
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (!invoiceSearch) return true;
    const search = invoiceSearch.toLowerCase();
    return (
      invoice.invoice_number?.toLowerCase().includes(search) ||
      invoice.customer_email?.toLowerCase().includes(search) ||
      invoice.tracking_numbers?.toLowerCase().includes(search)
    );
  });

  const warehousePackages = packages.filter(p => p.status === 'received_by_dac');
  const transitPackages = packages.filter(p => p.status === 'in_transit');

  const deliveredPackages = packages.filter(p => p.status === 'delivered');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'customers', label: 'Customers', icon: Users, count: customers.length },
    { id: 'packages', label: 'Packages', icon: Package, count: packages.length },
    { id: 'bulk-upload', label: 'Bulk Upload', icon: UploadCloud },
    { id: 'invoices', label: 'Invoices', icon: FileText, count: invoices.length },
    { id: 'broadcast', label: 'Broadcast', icon: Send },
    { id: 'feedback', label: 'Feedback', icon: MessageSquare, count: feedbackList.length },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          w-64 bg-slate-900 text-white flex flex-col
          transition-transform duration-300 ease-in-out
          fixed lg:sticky top-0 left-0 h-screen z-50 flex-shrink-0
        `}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/95 rounded-lg p-2">
              <img src="/dac-logo.png" alt="DAC" className="h-8 w-8 object-contain" />
            </div>
            <span className="font-bold text-lg">DAC SHIPPING</span>
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto overscroll-contain">
          {menuItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => { setActiveSection(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                activeSection === item.id 
                  ? 'bg-orange-500/10 text-orange-500 border-r-2 border-orange-500' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.count !== undefined && (
                <span className="text-xs bg-slate-700 px-2 py-0.5 rounded-full">{item.count}</span>
              )}
            </button>
          ))}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-slate-800 flex-shrink-0">
          <p className="text-xs text-slate-500 mb-2 truncate">{user?.email}</p>
          <Button
            data-testid="admin-logout-btn"
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="h-5 w-5" />
            <span className="ml-3">Logout</span>
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0 min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 lg:px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                data-testid="admin-menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg lg:hidden"
                aria-label="Toggle menu"
              >
                <Menu className="h-5 w-5 text-slate-600" />
              </button>
              <h1 className="text-lg lg:text-xl font-bold text-slate-900 capitalize">{activeSection}</h1>
            </div>
            <div className="flex items-center gap-2">
              {activeSection === 'packages' && (
                <Button data-testid="add-package-btn" onClick={() => setShowPackageDialog(true)} className="bg-orange-500 hover:bg-orange-600 text-sm px-3 py-2">
                  <Plus className="h-4 w-4 lg:mr-2" /> <span className="hidden lg:inline">Add Package</span>
                </Button>
              )}
              {activeSection === 'invoices' && (
                <Button data-testid="add-invoice-btn" onClick={() => setShowInvoiceDialog(true)} className="bg-orange-500 hover:bg-orange-600 text-sm px-3 py-2">
                  <Plus className="h-4 w-4 lg:mr-2" /> <span className="hidden lg:inline">Create Invoice</span>
                </Button>
              )}
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {/* OVERVIEW SECTION */}
          {activeSection === 'overview' && (
            <div className="space-y-6">
              {/* Address Migration */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="migration-banner">
                <div>
                  <p className="font-semibold text-amber-900">Migrate Customer Addresses</p>
                  <p className="text-sm text-amber-700">Rewrites every customer to <strong>836 NE 30th CT Building B, Oakland Park, FL 33334-2625</strong>. <strong>DAC339</strong> is the exception — kept at <strong>5161 NW 79th Ave 5 D, Doral FL 33166</strong>. Ensures every name ends with "DAC". Safe to re-run.</p>
                </div>
                <Button
                  data-testid="migrate-addresses-btn"
                  onClick={handleMigrateAddresses}
                  disabled={migratingAddresses}
                  className="bg-amber-600 hover:bg-amber-700 flex-shrink-0"
                >
                  {migratingAddresses ? 'Migrating...' : 'Migrate All Addresses'}
                </Button>
              </div>

              {/* Signup Pause Toggle */}
              <div className="bg-sky-50 border border-sky-500/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="signup-pause-banner">
                <div>
                  <p className="font-semibold text-sky-100">New Customer Signups</p>
                  <p className="text-sm text-sky-300">
                    {signupsPaused
                      ? 'Signups are currently PAUSED. New customers will see a notice on the signup page.'
                      : 'Signups are currently OPEN. New customers can create accounts.'}
                  </p>
                </div>
                <Button
                  data-testid="toggle-signups-btn"
                  onClick={handleToggleSignups}
                  disabled={togglingSignups}
                  className={signupsPaused ? 'bg-emerald-600 hover:bg-emerald-700 flex-shrink-0' : 'bg-sky-600 hover:bg-sky-700 flex-shrink-0'}
                >
                  {togglingSignups ? 'Updating…' : signupsPaused ? 'Resume Signups' : 'Pause Signups'}
                </Button>
              </div>

              {/* Current Shipment */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="current-shipment-banner">
                <div>
                  <p className="font-semibold text-indigo-900">Current Shipment</p>
                  <p className="text-sm text-indigo-700">
                    {currentShipmentInfo
                      ? <>Active: <span className="font-semibold">{currentShipmentInfo.name}</span> · {currentShipmentInfo.package_count || 0} packages attached. New invoices pull from this shipment only.</>
                      : 'No active shipment. Invoice creation will pull every unpaid in-transit package until you start one.'}
                  </p>
                </div>
                <Button
                  data-testid="new-shipment-btn"
                  onClick={handleStartNewShipment}
                  disabled={shipmentBusy}
                  className="bg-indigo-600 hover:bg-indigo-700 flex-shrink-0"
                >
                  {shipmentBusy ? 'Starting…' : currentShipmentInfo ? 'Close & Start New Shipment' : 'Start First Shipment'}
                </Button>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Customers</p>
                        <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">{customers.length}</p>
                      </div>
                      <div className="h-10 w-10 lg:h-12 lg:w-12 bg-blue-50 rounded-xl flex items-center justify-center">
                        <Users className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Warehouse</p>
                        <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">{warehousePackages.length}</p>
                      </div>
                      <div className="h-10 w-10 lg:h-12 lg:w-12 bg-amber-50 rounded-xl flex items-center justify-center">
                        <Package className="h-5 w-5 lg:h-6 lg:w-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">In Transit</p>
                        <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">{transitPackages.length}</p>
                      </div>
                      <div className="h-10 w-10 lg:h-12 lg:w-12 bg-orange-50 rounded-xl flex items-center justify-center">
                        <Package className="h-5 w-5 lg:h-6 lg:w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Unpaid</p>
                        <p className="text-2xl lg:text-3xl font-bold text-slate-900 mt-1">{invoices.filter(i => i.status !== 'paid').length}</p>
                      </div>
                      <div className="h-10 w-10 lg:h-12 lg:w-12 bg-rose-50 rounded-xl flex items-center justify-center">
                        <DollarSign className="h-5 w-5 lg:h-6 lg:w-6 text-rose-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base lg:text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2 lg:gap-3">
                  <Button onClick={() => { setActiveSection('packages'); setShowPackageDialog(true); }} className="bg-orange-500 hover:bg-orange-600 text-sm">
                    <Plus className="h-4 w-4 mr-1" /> Add Package
                  </Button>
                  <Button onClick={() => { setActiveSection('invoices'); setShowInvoiceDialog(true); }} variant="outline" className="text-sm">
                    <FileText className="h-4 w-4 mr-1" /> Create Invoice
                  </Button>
                  <Button onClick={() => setActiveSection('broadcast')} variant="outline" className="text-sm">
                    <Send className="h-4 w-4 mr-1" /> Broadcast
                  </Button>
                </CardContent>
              </Card>

              {/* Recent Packages */}
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base lg:text-lg">Recent Packages</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {packages.slice(0, 5).map(pkg => (
                      <div key={pkg.package_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="min-w-0 flex-1">
                          <p className="font-mono font-semibold text-slate-900 text-sm truncate">{pkg.tracking_number}</p>
                          <p className="text-xs text-slate-500 truncate">{pkg.customer_email}</p>
                        </div>
                        <Badge className={`${getStatusColor(pkg.status)} border text-xs ml-2 flex-shrink-0`}>
                          {getStatusLabel(pkg.status)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CUSTOMERS SECTION */}
          {activeSection === 'customers' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input 
                    data-testid="customer-search-input"
                    placeholder="Search customers..." 
                    className="pl-10" 
                    value={customerListSearch} 
                    onChange={e => setCustomerListSearch(e.target.value)} 
                  />
                </div>
              </div>

              {/* Mobile Cards View */}
              <div className="lg:hidden space-y-3">
                {filteredCustomerList.map(customer => (
                  <Card key={customer.user_id} className="bg-white border border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">{customer.name}</p>
                          <p className="text-sm text-slate-500">{customer.email}</p>
                        </div>
                        <span className="font-mono text-orange-600 font-semibold text-sm">
                          {customer.us_address?.unique_id || customer.us_address?.address_line2}
                        </span>
                      </div>
                      {customer.phone && <p className="text-sm text-slate-600 mb-1">📱 {customer.phone}</p>}
                      {(customer.guyana_address || customer.address) && (
                        <p className="text-sm text-slate-600 mb-2">📍 {customer.guyana_address || customer.address}</p>
                      )}
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Button variant="outline" size="sm" onClick={() => handleEditCustomer(customer)} className="flex-1">
                          <Pencil className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 flex-1" onClick={() => setDeleteCustomerConfirm(customer)}>
                          <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <Card className="bg-white border border-slate-200 shadow-sm hidden lg:block">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                          <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Contact</th>
                          <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Guyana Address</th>
                          <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">ID</th>
                          <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCustomerList.map(customer => (
                          <tr key={customer.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <p className="font-semibold text-slate-900">{customer.name}</p>
                              <p className="text-sm text-slate-500">{customer.email}</p>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">{customer.phone || '-'}</td>
                            <td className="py-3 px-4 text-sm text-slate-600 max-w-xs">
                              {customer.guyana_address || customer.address || '-'}
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-mono text-orange-600 font-semibold">
                                {customer.us_address?.unique_id || customer.us_address?.address_line2}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-2">
                                <Button variant="ghost" size="sm" onClick={() => handleEditCustomer(customer)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => setDeleteCustomerConfirm(customer)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* PACKAGES SECTION */}
          {activeSection === 'packages' && (
            <div className="space-y-4 lg:space-y-6">
              {/* Bulk Update */}
              <Card className="bg-orange-50 border border-orange-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base lg:text-lg">Bulk Update to Transit</CardTitle>
                  <CardDescription className="text-sm">Move warehouse packages to in-transit</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                    <div className="w-full sm:w-auto">
                      <Label className="text-xs">Start Date</Label>
                      <Input type="date" value={transitStartDate} onChange={e => setTransitStartDate(e.target.value)} className="w-full sm:w-40" />
                    </div>
                    <div className="w-full sm:w-auto">
                      <Label className="text-xs">End Date</Label>
                      <Input type="date" value={transitEndDate} onChange={e => setTransitEndDate(e.target.value)} className="w-full sm:w-40" />
                    </div>
                    <Button onClick={handleBulkTransitUpdate} disabled={updatingTransit || !transitStartDate || !transitEndDate} className="bg-orange-500 hover:bg-orange-600 w-full sm:w-auto">
                      {updatingTransit ? 'Updating...' : `Update ${warehousePackages.length}`}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Mobile Cards View */}
              <div className="lg:hidden space-y-3">
                {packages.map(pkg => (
                  <Card key={pkg.package_id} className="bg-white border border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2 gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          {pkg.photo_path ? (
                            <img
                              src={`${API}/files/${pkg.photo_path}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                              alt="Package"
                              data-testid={`admin-package-photo-${pkg.tracking_number}`}
                              onClick={() => window.open(`${API}/files/${pkg.photo_path}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank')}
                              className="h-14 w-14 object-cover rounded-lg border border-slate-200 cursor-pointer flex-shrink-0"
                            />
                          ) : null}
                          <p className="font-mono font-semibold text-slate-900 text-sm break-all min-w-0">{pkg.tracking_number}</p>
                        </div>
                        <Badge className={`${getStatusColor(pkg.status)} border text-xs flex-shrink-0`}>
                          {getStatusLabel(pkg.status)}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{pkg.customer_email}</p>
                      <div className="flex gap-2">
                        <Select onValueChange={val => handleUpdatePackageStatus(pkg.package_id, val)}>
                          <SelectTrigger className="flex-1 h-9">
                            <SelectValue placeholder="Update Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="received_by_dac">Received</SelectItem>
                            <SelectItem value="in_transit">In Transit</SelectItem>
                            <SelectItem value="arrived_in_guyana">Arrived GY</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => setDeletePackageConfirm(pkg)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <Card className="bg-white border border-slate-200 shadow-sm hidden lg:block">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Tracking</th>
                          <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                          <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Status</th>
                          <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {packages.map(pkg => (
                          <tr key={pkg.package_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                {pkg.photo_path ? (
                                  <img
                                    src={`${API}/files/${pkg.photo_path}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                                    alt="Package"
                                    onClick={() => window.open(`${API}/files/${pkg.photo_path}?auth=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank')}
                                    className="h-10 w-10 object-cover rounded border border-slate-200 cursor-pointer flex-shrink-0"
                                  />
                                ) : null}
                                <span className="font-mono font-semibold text-slate-900">{pkg.tracking_number}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-slate-600">{pkg.customer_email}</td>
                            <td className="py-3 px-4">
                              <Badge className={`${getStatusColor(pkg.status)} border`}>
                                {getStatusLabel(pkg.status)}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-2">
                                <Select onValueChange={val => handleUpdatePackageStatus(pkg.package_id, val)}>
                                  <SelectTrigger className="w-32 h-8">
                                    <SelectValue placeholder="Update" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="received_by_dac">Received</SelectItem>
                                    <SelectItem value="in_transit">In Transit</SelectItem>
                                    <SelectItem value="arrived_in_guyana">Arrived GY</SelectItem>
                                    <SelectItem value="delivered">Delivered</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDeletePackageConfirm(pkg)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* INVOICES SECTION */}
          {activeSection === 'invoices' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search invoices..." className="pl-10" value={invoiceSearch} onChange={e => setInvoiceSearch(e.target.value)} />
              </div>

              {filteredInvoices.length === 0 ? (
                <Card className="bg-white border border-slate-200"><CardContent className="py-12 text-center text-slate-500">No invoices found</CardContent></Card>
              ) : (
                <div className="space-y-3">
                  {filteredInvoices.map(invoice => {
                    const customer = customers.find(c => c.email === invoice.customer_email);
                    return (
                      <Card key={invoice.invoice_id} className="bg-white border border-slate-200 shadow-sm hover:shadow transition-shadow" data-testid={`admin-invoice-${invoice.invoice_number}`}>
                        <CardContent className="p-0">
                          <div className="flex flex-col sm:flex-row">
                            {/* Left — Invoice Info */}
                            <div className="flex-1 p-4 sm:p-5">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <p className="font-mono text-lg font-bold text-slate-900">{invoice.invoice_number}</p>
                                  <p className="text-sm text-slate-500">{customer?.name || invoice.customer_email}</p>
                                </div>
                                <Badge className={`text-xs ${invoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                  {invoice.status === 'paid' ? 'Paid' : 'Unpaid'}
                                </Badge>
                              </div>
                              {/* Line items preview */}
                              {invoice.items && invoice.items.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mb-3">
                                  {invoice.items.slice(0, 4).map((item, i) => (
                                    <span key={`${invoice.invoice_id || invoice.invoice_number}-item-${i}`} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{item.description}</span>
                                  ))}
                                  {invoice.items.length > 4 && <span className="text-xs text-slate-400">+{invoice.items.length - 4} more</span>}
                                </div>
                              )}
                              {invoice.tracking_numbers && (
                                <p className="text-xs font-mono text-slate-400 truncate">{invoice.tracking_numbers}</p>
                              )}
                            </div>
                            {/* Right — Amount & Actions */}
                            <div className="sm:border-l border-t sm:border-t-0 border-slate-100 p-4 sm:p-5 sm:w-56 flex flex-col justify-between bg-slate-50/50">
                              <div className="text-right sm:text-left mb-3">
                                <p className="text-2xl font-bold text-slate-900">${invoice.amount.toFixed(2)}</p>
                                <p className="text-xs text-slate-500">Issued: {formatInvoiceDate(invoice.created_at)}</p>
                                <p className="text-xs text-slate-500">Due: {formatInvoiceDate(invoice.due_date)}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  data-testid={`view-admin-invoice-${invoice.invoice_number}`}
                                  size="sm" variant="outline" className="flex-1"
                                  onClick={() => setViewAdminInvoice(invoice)}
                                >
                                  <Eye className="h-3.5 w-3.5 mr-1" /> View
                                </Button>
                                {invoice.status !== 'paid' && (
                                  <Button size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={() => handleMarkInvoicePaid(invoice.invoice_id)}>
                                    <Check className="h-3.5 w-3.5 mr-1" /> Paid
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => {
                                  const phone = customer?.phone?.replace(/\D/g, '') || '';
                                  const message = `*DAC SHIPPING - Invoice ${invoice.invoice_number}*\n\nAmount: $${invoice.amount.toFixed(2)}\nDue: ${invoice.due_date}`;
                                  window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
                                }}>
                                  <Share2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* BULK UPLOAD SECTION */}
          {activeSection === 'bulk-upload' && (
            <BulkUpload customers={customers} />
          )}

          {/* FINANCE SECTION */}
          {activeSection === 'finance' && (
            <div className="space-y-4">
              {/* Finance Sub-tabs */}
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                {[
                  { id: 'balances', label: 'Balances', icon: DollarSign },
                  { id: 'statements', label: 'Statements', icon: FileText },
                  { id: 'payments', label: 'Payments', icon: CreditCard },
                  { id: 'outstanding', label: 'Outstanding', icon: ArrowUpDown },
                  { id: 'reports', label: 'Reports', icon: TrendingUp },
                ].map(tab => (
                  <Button
                    key={tab.id}
                    data-testid={`finance-tab-${tab.id}`}
                    variant={financeTab === tab.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFinanceTab(tab.id)}
                    className={`flex-shrink-0 ${financeTab === tab.id ? 'bg-slate-900' : ''}`}
                  >
                    <tab.icon className="h-3.5 w-3.5 mr-1.5" /> {tab.label}
                  </Button>
                ))}
              </div>

              {/* ===== BALANCES TAB ===== */}
              {financeTab === 'balances' && (
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <CardTitle className="text-lg">Customer Balance Summary</CardTitle>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf('balance-summary', 'balance_summary.pdf')}>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF
                      </Button>
                    </div>
                    <div className="relative max-w-sm mt-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input data-testid="finance-search" placeholder="Search customer..." className="pl-10" value={financeSearch} onChange={e => setFinanceSearch(e.target.value)} />
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {financeLoading ? (
                      <div className="py-12 text-center text-slate-500">Loading...</div>
                    ) : (
                      <>
                        {/* Mobile Cards */}
                        <div className="sm:hidden divide-y divide-slate-100">
                          {filteredBalanceSummary.map(s => (
                            <div key={s.customer_email} className={`p-4 ${s.closing_balance_gyd > 50000 ? 'bg-red-50/30' : ''}`} data-testid={`balance-row-${s.customer_email}`}>
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="text-sm font-medium text-slate-900">{s.customer_name}</p>
                                  <p className="text-xs text-slate-400">{s.customer_email}</p>
                                </div>
                                <Badge className={`text-xs ${s.status === 'Dr' ? 'bg-red-50 text-red-700 border border-red-200' : s.status === 'Cr' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                                  {s.status}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div>
                                  <p className="text-slate-500">Balance</p>
                                  <p className="font-mono font-medium">{s.invoice_balance_gyd.toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Credits</p>
                                  <p className="font-mono font-medium text-emerald-600">{s.available_credits_gyd.toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Closing</p>
                                  <p className={`font-mono font-bold ${s.closing_balance_gyd > 50000 ? 'text-red-600' : 'text-slate-900'}`}>{s.closing_balance_gyd.toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Desktop Table */}
                        <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-y border-slate-200">
                            <tr>
                              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Invoice Balance</th>
                              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Credits</th>
                              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Closing Balance</th>
                              <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBalanceSummary.map(s => (
                              <tr key={s.customer_email} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`balance-row-${s.customer_email}`}>
                                <td className="py-3 px-4">
                                  <p className="text-sm font-medium text-slate-900">{s.customer_name}</p>
                                  <p className="text-xs text-slate-400">{s.customer_email}</p>
                                </td>
                                <td className="py-3 px-4 text-right text-sm font-mono">{s.invoice_balance_gyd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                <td className="py-3 px-4 text-right text-sm font-mono text-emerald-600">{s.available_credits_gyd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                <td className={`py-3 px-4 text-right text-sm font-mono font-bold ${s.closing_balance_gyd > 50000 ? 'text-red-600' : 'text-slate-900'}`}>
                                  {s.closing_balance_gyd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="py-3 px-4 text-center">
                                  <Badge className={`text-xs ${s.status === 'Dr' ? 'bg-red-50 text-red-700 border border-red-200' : s.status === 'Cr' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                                    {s.status}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ===== STATEMENTS TAB ===== */}
              {financeTab === 'statements' && (
                <div className="space-y-4">
                  <Card className="bg-white border border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Customer Statement</CardTitle>
                      <CardDescription>Generate detailed financial statements</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col gap-3">
                        <Select onValueChange={val => setStatementCustomer(val)}>
                          <SelectTrigger className="w-full sm:w-64" data-testid="statement-customer-select">
                            <SelectValue placeholder="Select customer..." />
                          </SelectTrigger>
                          <SelectContent>
                            {customers.map(c => (
                              <SelectItem key={c.email} value={c.email}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                          <Input type="date" value={statementDateRange.start} onChange={e => setStatementDateRange(p => ({ ...p, start: e.target.value }))} className="flex-1" />
                          <Input type="date" value={statementDateRange.end} onChange={e => setStatementDateRange(p => ({ ...p, end: e.target.value }))} className="flex-1" />
                        </div>
                        <div className="flex gap-2">
                          <Button data-testid="generate-statement-btn" onClick={fetchCustomerStatement} disabled={!statementCustomer || financeLoading} className="flex-1 bg-slate-900">
                            Generate
                          </Button>
                          {statementData && (
                            <Button variant="outline" className="flex-1" onClick={() => downloadPdf(`customer-statement/${statementCustomer}?start_date=${statementDateRange.start}&end_date=${statementDateRange.end}`, `statement_${statementCustomer}.pdf`)}>
                              <Download className="h-3.5 w-3.5 mr-1.5" /> PDF
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {statementData && (
                    <Card className="bg-white border border-slate-200 shadow-sm">
                      <CardHeader className="pb-3 border-b">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{statementData.customer_name}</CardTitle>
                            <CardDescription>{statementData.customer_email}</CardDescription>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Closing Balance</p>
                            <p className={`text-xl font-bold font-mono ${statementData.closing_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {statementData.closing_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })} GYD
                            </p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Date</th>
                                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Ref</th>
                                <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Description</th>
                                <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Debit (GYD)</th>
                                <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Credit (GYD)</th>
                                <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Balance (GYD)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statementData.transactions.map((t, i) => (
                                <tr key={`${t.reference || 'txn'}-${t.date || ''}-${i}`} className="border-b border-slate-100">
                                  <td className="py-2.5 px-4 text-sm text-slate-600">{t.date?.slice(0, 10)}</td>
                                  <td className="py-2.5 px-4 text-sm font-mono text-slate-700">{t.reference}</td>
                                  <td className="py-2.5 px-4 text-sm text-slate-700">{t.description}</td>
                                  <td className="py-2.5 px-4 text-sm font-mono text-right text-red-600">{t.debit > 0 ? t.debit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</td>
                                  <td className="py-2.5 px-4 text-sm font-mono text-right text-emerald-600">{t.credit > 0 ? t.credit.toLocaleString(undefined, { minimumFractionDigits: 2 }) : ''}</td>
                                  <td className="py-2.5 px-4 text-sm font-mono text-right font-medium">{t.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                              <tr className="bg-slate-50 font-bold">
                                <td className="py-3 px-4 text-sm" colSpan="3">TOTALS</td>
                                <td className="py-3 px-4 text-sm font-mono text-right text-red-700">{statementData.total_debits.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                <td className="py-3 px-4 text-sm font-mono text-right text-emerald-700">{statementData.total_credits.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                <td className="py-3 px-4 text-sm font-mono text-right">{statementData.closing_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* ===== PAYMENTS TAB ===== */}
              {financeTab === 'payments' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-slate-900">Payment History</h3>
                    <Button data-testid="add-payment-btn" onClick={() => setShowPaymentDialog(true)} className="bg-slate-900">
                      <Plus className="h-4 w-4 mr-1.5" /> Record Payment
                    </Button>
                  </div>

                  {financePayments.length === 0 ? (
                    <Card className="bg-white border border-slate-200"><CardContent className="py-12 text-center text-slate-500">No payments recorded yet</CardContent></Card>
                  ) : (
                    <Card className="bg-white border border-slate-200 shadow-sm">
                      <CardContent className="p-0">
                        {/* Mobile Cards */}
                        <div className="sm:hidden divide-y divide-slate-100">
                          {financePayments.map(p => (
                            <div key={p.payment_id} className="p-4">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="text-sm font-medium text-slate-900">{p.customer_name}</p>
                                  <p className="text-xs font-mono text-slate-400">{p.payment_id}</p>
                                </div>
                                <p className="text-sm font-mono font-bold text-emerald-600">{p.amount.toLocaleString(undefined, { minimumFractionDigits: 0 })} GYD</p>
                              </div>
                              <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                                <span>{p.date}</span>
                                <Badge className="text-xs bg-slate-100 text-slate-600 border border-slate-200">{p.method}</Badge>
                                {p.applied_to && <span className="font-mono">{p.applied_to}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Desktop Table */}
                        <div className="hidden sm:block overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Payment ID</th>
                                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Amount (GYD)</th>
                                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Date</th>
                                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Applied To</th>
                                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Method</th>
                              </tr>
                            </thead>
                            <tbody>
                              {financePayments.map(p => (
                                <tr key={p.payment_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                                  <td className="py-3 px-4 text-sm font-mono font-medium text-slate-900">{p.payment_id}</td>
                                  <td className="py-3 px-4">
                                    <p className="text-sm font-medium text-slate-900">{p.customer_name}</p>
                                    <p className="text-xs text-slate-400">{p.customer_email}</p>
                                  </td>
                                  <td className="py-3 px-4 text-right text-sm font-mono font-bold text-emerald-600">{p.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                  <td className="py-3 px-4 text-sm text-slate-600">{p.date}</td>
                                  <td className="py-3 px-4 text-sm font-mono text-slate-600">{p.applied_to || '—'}</td>
                                  <td className="py-3 px-4">
                                    <Badge className="text-xs bg-slate-100 text-slate-600 border border-slate-200">{p.method}</Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* ===== OUTSTANDING TAB ===== */}
              {financeTab === 'outstanding' && (
                <Card className="bg-white border border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <CardTitle className="text-lg">Outstanding Balances</CardTitle>
                        <CardDescription>Customers with unpaid invoices</CardDescription>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => downloadPdf('outstanding', 'outstanding_balances.pdf')}>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    {outstandingBalances.length === 0 ? (
                      <div className="py-12 text-center text-slate-500">No outstanding balances</div>
                    ) : (
                      <>
                        {/* Mobile Cards */}
                        <div className="sm:hidden divide-y divide-slate-100">
                          {outstandingBalances.map(o => (
                            <div key={o.customer_email} className={`p-4 ${o.total_outstanding_gyd > 50000 ? 'bg-red-50/30' : ''}`}>
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="text-sm font-medium text-slate-900">{o.customer_name}</p>
                                  <p className="text-xs text-slate-400">{o.customer_email}</p>
                                </div>
                                <p className={`text-sm font-mono font-bold ${o.total_outstanding_gyd > 50000 ? 'text-red-600' : 'text-slate-900'}`}>
                                  {o.total_outstanding_gyd.toLocaleString(undefined, { minimumFractionDigits: 0 })} GYD
                                </p>
                              </div>
                              <div className="flex gap-4 text-xs text-slate-500">
                                <span>{o.unpaid_count} unpaid invoices</span>
                                <span>Last payment: {o.last_payment_date}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Desktop Table */}
                        <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-y border-slate-200">
                            <tr>
                              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Outstanding (GYD)</th>
                              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Unpaid Invoices</th>
                              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Last Payment</th>
                            </tr>
                          </thead>
                          <tbody>
                            {outstandingBalances.map(o => (
                              <tr key={o.customer_email} className={`border-b border-slate-100 hover:bg-slate-50/50 ${o.total_outstanding_gyd > 50000 ? 'bg-red-50/30' : ''}`}>
                                <td className="py-3 px-4">
                                  <p className="text-sm font-medium text-slate-900">{o.customer_name}</p>
                                  <p className="text-xs text-slate-400">{o.customer_email}</p>
                                </td>
                                <td className={`py-3 px-4 text-right text-sm font-mono font-bold ${o.total_outstanding_gyd > 50000 ? 'text-red-600' : 'text-slate-900'}`}>
                                  {o.total_outstanding_gyd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                                <td className="py-3 px-4 text-right text-sm text-slate-700">{o.unpaid_count}</td>
                                <td className="py-3 px-4 text-sm text-slate-600">{o.last_payment_date}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ===== REPORTS TAB ===== */}
              {financeTab === 'reports' && financialReport && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button
                      data-testid="reports-download-pdf"
                      size="sm"
                      variant="outline"
                      onClick={() => downloadPdf('reports', 'financial_report.pdf')}
                    >
                      <FileText className="h-4 w-4 mr-2" /> Download PDF
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <Card className="bg-white border border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Revenue</p>
                        <p className="text-lg font-bold font-mono text-slate-900">{financialReport.total_revenue_gyd.toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
                        <p className="text-xs text-slate-400">GYD</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-white border border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Collected</p>
                        <p className="text-lg font-bold font-mono text-emerald-600">{financialReport.total_payments_gyd.toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
                        <p className="text-xs text-slate-400">GYD</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-white border border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Outstanding</p>
                        <p className="text-lg font-bold font-mono text-red-600">{financialReport.total_outstanding_gyd.toLocaleString(undefined, { minimumFractionDigits: 0 })}</p>
                        <p className="text-xs text-slate-400">GYD</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-white border border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Invoices</p>
                        <p className="text-lg font-bold text-slate-900">{financialReport.total_invoices}</p>
                        <p className="text-xs text-slate-400">{financialReport.paid_invoices} paid / {financialReport.unpaid_invoices} unpaid</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-white border border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Shipments</p>
                        <p className="text-lg font-bold text-slate-900">{financialReport.total_shipments}</p>
                        <p className="text-xs text-slate-400">Total tracked</p>
                      </CardContent>
                    </Card>
                  </div>
                  <Card className="bg-white border border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Collection Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${financialReport.total_revenue_gyd > 0 ? Math.min((financialReport.total_payments_gyd / financialReport.total_revenue_gyd * 100), 100) : 0}%` }}
                        />
                      </div>
                      <p className="text-sm text-slate-500 mt-2">
                        {financialReport.total_revenue_gyd > 0 ? ((financialReport.total_payments_gyd / financialReport.total_revenue_gyd) * 100).toFixed(1) : 0}% collected
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}


          {/* BROADCAST SECTION */}
          {activeSection === 'broadcast' && (
            <Card className="bg-white border border-slate-200 shadow-sm max-w-2xl">
              <CardHeader>
                <CardTitle>Send Broadcast</CardTitle>
                <CardDescription>Send email to all your customers at once</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBroadcastMessage} className="space-y-4">
                  {/* Quick Select Buttons */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Label className="text-sm font-medium mb-3 block">Quick Select</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        type="button" 
                        variant="outline"
                        className="bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
                        onClick={() => {
                          const allEmails = customers.map(c => c.email).join('\n');
                          setBroadcastForm({ ...broadcastForm, emails: allEmails });
                        }}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        All Customers ({customers.length})
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => setBroadcastForm({ ...broadcastForm, emails: '' })}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Recipients</Label>
                      <span className="text-sm text-slate-500">
                        {broadcastForm.emails.split(/[\n,]/).filter(e => e.trim()).length} email(s)
                      </span>
                    </div>
                    <textarea
                      className="w-full min-h-[100px] p-3 border border-slate-200 rounded-lg text-sm font-mono"
                      placeholder="Click 'All Customers' above or enter emails manually"
                      value={broadcastForm.emails}
                      onChange={e => setBroadcastForm({ ...broadcastForm, emails: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Subject *</Label>
                    <Input 
                      value={broadcastForm.subject} 
                      onChange={e => setBroadcastForm({ ...broadcastForm, subject: e.target.value })}
                      placeholder="e.g., Important Update from DAC Shipping"
                      required
                    />
                  </div>
                  <div>
                    <Label>Message *</Label>
                    <textarea
                      className="w-full min-h-[150px] p-3 border border-slate-200 rounded-lg"
                      placeholder="Type your message here..."
                      value={broadcastForm.message}
                      onChange={e => setBroadcastForm({ ...broadcastForm, message: e.target.value })}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={sendingBroadcast || !broadcastForm.emails.trim() || !broadcastForm.subject.trim() || !broadcastForm.message.trim()} 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    {sendingBroadcast ? 'Sending...' : `Send to ${broadcastForm.emails.split(/[\n,]/).filter(e => e.trim()).length} Recipients`}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {activeSection === 'feedback' && (
            <Card className="bg-white border border-slate-200 shadow-sm max-w-3xl" data-testid="admin-feedback-section">
              <CardHeader>
                <CardTitle>Customer Feedback</CardTitle>
                <CardDescription>Feedback submitted by customers. Sender identity is shown here for your reference.</CardDescription>
              </CardHeader>
              <CardContent>
                {feedbackList.length === 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="feedback-empty">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    No feedback yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {feedbackList.map(fb => (
                      <div key={fb.feedback_id} className="p-4 rounded-lg border border-slate-200 bg-slate-50" data-testid={`feedback-item-${fb.feedback_id}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <p className="font-semibold text-slate-900">{fb.customer_name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{fb.customer_email}</p>
                          </div>
                          <span className="text-xs text-slate-400 whitespace-nowrap">{formatInvoiceDate(fb.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{fb.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* ADD PACKAGE DIALOG */}
      <Dialog open={showPackageDialog} onOpenChange={open => { if (!open) stopScanner(); setShowPackageDialog(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Package</DialogTitle>
            <DialogDescription>Register a new package</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePackage} className="space-y-4">
            <div>
              <Label>Package / Label Photo</Label>
              <div className="mt-1 flex items-start gap-3">
                <label
                  htmlFor="package-photo-input"
                  className="flex-1 cursor-pointer border-2 border-dashed border-slate-300 rounded-lg p-3 text-center hover:border-orange-400 hover:bg-orange-50 transition-colors"
                  data-testid="package-photo-upload-label"
                >
                  {uploadingPackagePhoto ? (
                    <p className="text-sm text-slate-600">Uploading & reading label…</p>
                  ) : (
                    <>
                      <Camera className="h-5 w-5 mx-auto text-slate-400 mb-1" />
                      <p className="text-xs text-slate-600">Tap to upload photo — tracking number auto-detects</p>
                    </>
                  )}
                  <input
                    id="package-photo-input"
                    data-testid="package-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={e => handlePackagePhotoSelect(e.target.files?.[0])}
                  />
                </label>
                {packagePhotoPreview && (
                  <img
                    src={packagePhotoPreview}
                    alt="Package"
                    className="h-20 w-20 object-cover rounded-lg border border-slate-200"
                    data-testid="package-photo-preview"
                  />
                )}
              </div>
            </div>

            <div>
              <Label>Customer *</Label>
              <Input
                placeholder="Search by name, email, or ID..."
                value={packageCustomerSearch}
                onChange={e => setPackageCustomerSearch(e.target.value)}
              />
              {packageCustomerSearch && filteredPackageCustomers.length > 0 && (
                <div className="border rounded-lg mt-2 max-h-40 overflow-y-auto">
                  {filteredPackageCustomers.slice(0, 5).map(c => (
                    <div
                      key={c.user_id}
                      className={`p-2 cursor-pointer hover:bg-orange-50 ${packageForm.customer_email === c.email ? 'bg-orange-100' : ''}`}
                      onClick={() => {
                        setPackageForm({ ...packageForm, customer_email: c.email });
                        setPackageCustomerSearch(c.name);
                      }}
                    >
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.email}</p>
                    </div>
                  ))}
                </div>
              )}
              {packageForm.customer_email && (
                <p className="text-sm text-emerald-600 mt-1">✓ {packageForm.customer_email}</p>
              )}
            </div>

            <div>
              <Label>Tracking Number *</Label>
              <div className="flex gap-2">
                <Input
                  value={packageForm.tracking_number}
                  onChange={e => setPackageForm({ ...packageForm, tracking_number: e.target.value })}
                  required
                />
                <Button type="button" variant="outline" onClick={scanning ? stopScanner : startScanner}>
                  {scanning ? <X className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
                </Button>
              </div>
              <div className={`mt-2 ${scanning ? 'block' : 'hidden'}`}>
                <div id="barcode-scanner" className="w-full border-2 border-orange-500 rounded-lg overflow-hidden" style={{ minHeight: '200px' }}></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Carrier</Label>
                <Input value={packageForm.carrier} onChange={e => setPackageForm({ ...packageForm, carrier: e.target.value })} />
              </div>
              <div>
                <Label>Weight</Label>
                <Input value={packageForm.weight} onChange={e => setPackageForm({ ...packageForm, weight: e.target.value })} />
              </div>
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={!packageForm.customer_email || !packageForm.tracking_number}>
              Create Package
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* CREATE INVOICE DIALOG */}
      <Dialog open={showInvoiceDialog} onOpenChange={open => { setShowInvoiceDialog(open); if (!open) { setCustomerSearch(''); setCustomItems([]); setSelectedPackageIds([]); setCustomerPackages([]); setIncludeOldShipments(false); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>Generate invoice for customer</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateInvoice} className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Input
                placeholder="Search customer..."
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
              />
              {customerSearch && filteredCustomers.length > 0 && (
                <div className="border rounded-lg mt-2 max-h-40 overflow-y-auto">
                  {filteredCustomers.slice(0, 5).map(c => (
                    <div
                      key={c.user_id}
                      className={`p-2 cursor-pointer hover:bg-orange-50 ${invoiceForm.customer_email === c.email ? 'bg-orange-100' : ''}`}
                      onClick={() => {
                        setInvoiceForm({ ...invoiceForm, customer_email: c.email });
                        setCustomerSearch(c.name);
                        setIncludeOldShipments(false);
                        fetchCustomerPackages(c.email, false);
                      }}
                    >
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.email}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {invoiceForm.customer_email && (
              <div
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer"
                data-testid="include-older-shipments-toggle"
                onClick={() => {
                  const next = !includeOldShipments;
                  setIncludeOldShipments(next);
                  fetchCustomerPackages(invoiceForm.customer_email, next);
                }}
              >
                <div className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${includeOldShipments ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300 bg-white'}`}>
                  {includeOldShipments && <Check className="h-3.5 w-3.5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900">Include packages from older shipments</p>
                  <p className="text-xs text-slate-500">By default only the current shipment's in-transit packages are pulled.</p>
                </div>
              </div>
            )}

            {invoiceForm.customer_email && customerPackages.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">In-Transit Packages — select to include:</Label>
                <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
                  {customerPackages.map(pkg => {
                    const isSelected = selectedPackageIds.includes(pkg.tracking_number);
                    return (
                      <div
                        key={pkg.tracking_number}
                        data-testid={`invoice-pkg-${pkg.tracking_number}`}
                        className={`flex items-center gap-3 p-3 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'bg-white hover:bg-slate-50'}`}
                        onClick={() => togglePackageSelection(pkg.tracking_number, pkg.weight)}
                      >
                        <div className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                          {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-mono font-medium text-slate-900 truncate">{pkg.tracking_number}</p>
                          <p className="text-xs text-slate-500">{pkg.carrier || 'Unknown'} — {pkg.weight || 'N/A'}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500">{selectedPackageIds.length} of {customerPackages.length} selected</p>
              </div>
            )}
            {invoiceForm.customer_email && customerPackages.length === 0 && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-500 text-center">
                {includeOldShipments
                  ? 'No in-transit packages for this customer'
                  : "No in-transit packages on the current shipment. Toggle \u201CInclude packages from older shipments\u201D above to pull older ones."}
              </div>
            )}

            {/* Editable packages/tracking field */}
            <div>
              <Label className="text-sm font-medium">Packages on Invoice</Label>
              <textarea
                data-testid="invoice-tracking-textarea"
                className="w-full mt-1 p-3 bg-[#060A14] text-slate-100 placeholder:text-slate-500 border border-slate-200 rounded-lg text-sm font-mono min-h-[60px] focus:outline-none focus:ring-2 focus:ring-orange-300"
                placeholder="Tracking numbers or package notes (edit freely)..."
                value={invoiceTrackingNumbers}
                onChange={e => setInvoiceTrackingNumbers(e.target.value)}
              />
              <p className="text-xs text-slate-400 mt-1">Auto-filled from selected packages. You can edit or type additional info.</p>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-semibold">Invoice Items</Label>
              
              <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                <p className="text-sm font-medium">Freight</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" step="0.1" placeholder="Weight (lbs)" value={invoiceItems.freight_qty} onChange={e => setInvoiceItems({ ...invoiceItems, freight_qty: e.target.value })} />
                  <Input type="number" step="0.01" placeholder="Rate $/lb" value={invoiceItems.freight_rate} onChange={e => setInvoiceItems({ ...invoiceItems, freight_rate: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Handling</p>
                  <Input type="number" step="0.01" placeholder="$0.00" value={invoiceItems.handling} onChange={e => setInvoiceItems({ ...invoiceItems, handling: e.target.value })} />
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Duty</p>
                  <Input type="number" step="0.01" placeholder="$0.00" value={invoiceItems.duty} onChange={e => setInvoiceItems({ ...invoiceItems, duty: e.target.value })} />
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium mb-2">Insurance</p>
                <Input type="number" step="0.01" placeholder="$0.00" value={invoiceItems.insurance} onChange={e => setInvoiceItems({ ...invoiceItems, insurance: e.target.value })} />
              </div>

              {/* Custom Items & Credits */}
              {customItems.map((item, index) => {
                const isCredit = item.type === 'credit';
                return (
                  <div key={item.id} className={`p-3 rounded-lg border ${isCredit ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`invoice-${isCredit ? 'credit' : 'custom'}-item-${index}`}>
                    <div className="flex justify-between items-center mb-2">
                      <p className={`text-sm font-medium ${isCredit ? 'text-emerald-600' : ''}`}>{isCredit ? 'Credit / Discount (−)' : 'Custom Item'}</p>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomItem(index)} className="text-red-600 h-6 w-6 p-0">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder={isCredit ? 'Reason (e.g. Credit balance)' : 'Item name'} value={item.name} onChange={e => updateCustomItem(index, 'name', e.target.value)} data-testid={`invoice-item-name-${index}`} />
                      <div className="relative">
                        {isCredit && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 font-bold z-10">−</span>}
                        <Input type="number" step="0.01" min="0" placeholder="$0.00" value={item.amount} onChange={e => updateCustomItem(index, 'amount', e.target.value)} className={isCredit ? 'pl-7' : ''} data-testid={`invoice-item-amount-${index}`} />
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" onClick={() => addCustomItem('charge')} data-testid="add-custom-item-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Item
                </Button>
                <Button type="button" variant="outline" onClick={() => addCustomItem('credit')} className="border-emerald-300 text-emerald-600 hover:bg-emerald-50" data-testid="add-credit-item-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Credit (−)
                </Button>
              </div>
            </div>

            <div className="p-4 bg-orange-50 border-2 border-orange-500 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total</span>
                <span className={`text-2xl font-bold ${parseFloat(calculateInvoiceTotal()) < 0 ? 'text-red-400' : 'text-orange-600'}`}>${calculateInvoiceTotal()}</span>
              </div>
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" disabled={!invoiceForm.customer_email}>
              Create Invoice
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ADMIN INVOICE VIEW MODAL */}
      <Dialog open={!!viewAdminInvoice} onOpenChange={(open) => { if (!open) setViewAdminInvoice(null); }}>
        <DialogContent className="max-w-lg p-0 max-h-[92vh] overflow-y-auto">
          <DialogTitle className="sr-only">Invoice Details</DialogTitle>
          {viewAdminInvoice && (() => {
            const customer = customers.find(c => c.email === viewAdminInvoice.customer_email);
            return (
              <div data-testid="admin-invoice-detail-modal">
                {/* Header */}
                <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-[#0C1427] via-[#0C1427] to-[#1D4ED8]/30">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="bg-white/95 rounded-lg p-1.5"><img src="/dac-logo.png" alt="DAC" className="h-9 w-9 object-contain" /></div>
                      <div>
                        <h2 className="text-lg font-black tracking-tight text-white leading-none">DAC SHIPPING</h2>
                        <p className="text-slate-400 text-[11px] mt-1">USA → Guyana Shipping</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Invoice</p>
                      <p className="text-xl font-mono font-bold text-[#FF6B35]">{viewAdminInvoice.invoice_number}</p>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF6B35] via-[#2563EB] to-transparent" />
                </div>
                {/* Body */}
                <div className="px-6 py-5 space-y-5">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <Badge className={`text-sm px-3 py-1 ${viewAdminInvoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                      {viewAdminInvoice.status === 'paid' ? 'Paid' : 'Payment Due'}
                    </Badge>
                    <div className="flex gap-2">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Issued</p>
                        <p className="text-sm font-semibold text-slate-100">{formatInvoiceDate(viewAdminInvoice.created_at)}</p>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-center">
                        <p className="text-[10px] uppercase tracking-wider text-slate-400">Due</p>
                        <p className="text-sm font-semibold text-amber-300">{formatInvoiceDate(viewAdminInvoice.due_date)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1">Bill To</p>
                    <p className="font-semibold text-slate-900">{customer?.name || 'Customer'}</p>
                    <p className="text-sm text-slate-600">{viewAdminInvoice.customer_email}</p>
                    {customer?.phone && <p className="text-sm text-slate-500">{customer.phone}</p>}
                  </div>
                  {viewAdminInvoice.tracking_numbers && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <p className="text-xs uppercase tracking-wider text-blue-500 font-medium mb-1">Packages</p>
                      <p className="text-sm font-mono text-blue-800">{viewAdminInvoice.tracking_numbers}</p>
                    </div>
                  )}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Description</th>
                          <th className="text-right py-2.5 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewAdminInvoice.items?.map((item, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="py-3 px-4 text-sm text-slate-700">{item.description}</td>
                            <td className={`py-3 px-4 text-sm font-medium text-right ${Number(item.amount) < 0 ? 'text-emerald-600' : 'text-slate-900'}`}>{Number(item.amount) < 0 ? `-$${Math.abs(Number(item.amount)).toFixed(2)}` : `$${Number(item.amount).toFixed(2)}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="bg-gradient-to-r from-[#0C1427] to-[#1D4ED8]/40 px-4 py-3.5 flex justify-between items-center border-t border-slate-200">
                      <span className="text-white font-semibold text-sm">Total Due</span>
                      <span className="text-[#FF6B35] text-2xl font-black">${viewAdminInvoice.amount.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    {viewAdminInvoice.status !== 'paid' && (
                      <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600" onClick={() => { handleMarkInvoicePaid(viewAdminInvoice.invoice_id); setViewAdminInvoice(null); }}>
                        <Check className="h-4 w-4 mr-2" /> Mark as Paid
                      </Button>
                    )}
                    <Button variant="outline" className="flex-1" onClick={() => {
                      const phone = customer?.phone?.replace(/\D/g, '') || '';
                      const message = `*DAC SHIPPING - Invoice ${viewAdminInvoice.invoice_number}*\n\nAmount: $${viewAdminInvoice.amount.toFixed(2)}\nDue: ${viewAdminInvoice.due_date}`;
                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
                    }}>
                      <Share2 className="h-4 w-4 mr-2" /> WhatsApp
                    </Button>
                  </div>
                  <div className="text-center text-xs text-slate-400 pt-2 border-t border-slate-100 space-y-0.5">
                    <p className="font-medium text-slate-500">DAC SHIPPING</p>
                    <p>22 Shell Road, Kitty, Georgetown, Guyana</p>
                    <p>WhatsApp: +1 592 635 0592</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>


      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Record a customer payment in GYD</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Customer</Label>
              <Select onValueChange={val => setPaymentForm(p => ({ ...p, customer_email: val }))}>
                <SelectTrigger data-testid="payment-customer-select">
                  <SelectValue placeholder="Select customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.email} value={c.email}>{c.name} ({c.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Amount (GYD)</Label>
                <Input data-testid="payment-amount" type="number" step="0.01" placeholder="0.00" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} />
              </div>
              <div>
                <Label className="text-sm font-medium">Date</Label>
                <Input type="date" value={paymentForm.date} onChange={e => setPaymentForm(p => ({ ...p, date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Apply to Invoice (optional)</Label>
              <Input data-testid="payment-applied-to" placeholder="e.g. INV-00001" value={paymentForm.applied_to} onChange={e => setPaymentForm(p => ({ ...p, applied_to: e.target.value }))} />
            </div>
            <div>
              <Label className="text-sm font-medium">Payment Method</Label>
              <Select value={paymentForm.method} onValueChange={val => setPaymentForm(p => ({ ...p, method: val }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="mobile">Mobile Payment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Notes</Label>
              <Input placeholder="Optional notes..." value={paymentForm.notes} onChange={e => setPaymentForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <Button data-testid="submit-payment-btn" onClick={handleCreatePayment} className="w-full bg-slate-900">
              Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Edit Customer Dialog */}
      <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCustomer} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={editCustomerForm.name} onChange={e => setEditCustomerForm({ ...editCustomerForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={editCustomerForm.email} onChange={e => setEditCustomerForm({ ...editCustomerForm, email: e.target.value })} />
            </div>
            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">Save</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Confirm */}
      <AlertDialog open={!!deleteCustomerConfirm} onOpenChange={() => setDeleteCustomerConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete <strong>{deleteCustomerConfirm?.name}</strong> and all their packages/invoices. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeleteCustomer(deleteCustomerConfirm?.user_id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Package Confirm */}
      <AlertDialog open={!!deletePackageConfirm} onOpenChange={() => setDeletePackageConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Package?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete package <strong className="font-mono">{deletePackageConfirm?.tracking_number}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDeletePackage(deletePackageConfirm?.package_id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
